// Libellés et formats partagés : suivi d'utilisation et fiche utilisateur (admin)

export const ROLE_META = {
  superadmin: { label: 'Super Admin', color: '#dc2626' },
  admin: { label: 'Administrateur', color: '#2563eb' },
  directeur: { label: 'Directeur', color: '#1d4ed8' },
  conducteur_travaux: { label: 'Conducteur de Travaux', color: '#7c3aed' },
  cost_controller: { label: 'Cost Controller', color: '#059669' },
  pointeur: { label: 'Pointeur Chantier', color: '#0891b2' },
  rh: { label: 'Ressources Humaines', color: '#d97706' },
  qhse_pays: { label: 'Responsable QHSE Pays', color: '#047857' },
  qhse_chantier: { label: 'Responsable QHSE Chantier', color: '#0284c7' },
  relais_qhse: { label: 'Relais QHSE', color: '#4f46e5' },
  user: { label: 'Utilisateur', color: '#475569' }
}

export const roleMeta = (role) => ROLE_META[role] || { label: role || '—', color: '#475569' }

// Nom de route → libellé lisible de la page
const PAGE_LABELS = {
  dashboard: 'Tableau de bord',
  projects: 'Projets',
  'project-detail': 'Fiche projet',
  'team-management': "Gestion d'équipe",
  profile: 'Mon profil',
  settings: 'Paramètres',
  resources: 'Ressources',
  planning: 'Planning',
  'planning-global': 'Planning global',
  'planning-tracking': 'Suivi planning',
  budget: 'Budget',
  personnel: 'Personnel',
  'personnel-report': 'Rapport personnel',
  'poste-reconciliation': 'Réconciliation postes',
  'activity-report': "Rapport d'activité",
  feedback: 'Feedback',
  materiel: 'Matériel',
  'materiel-reconciliation': 'Réconciliation matériel',
  'materiel-report': 'Rapport matériel',
  'budget-control': 'Contrôle budgétaire',
  'activity-liquidity': 'Activité & liquidité',
  'daily-expense-reports': 'Dépenses journalières',
  notifications: 'Notifications',
  reallocation: 'Réallocation',
  'site-reports': 'Rapports de chantier',
  'site-reports-create': 'Nouveau rapport de chantier',
  'site-reports-edit': 'Édition rapport de chantier',
  'site-report-view': 'Consultation rapport de chantier',
  'site-reports-validation': 'Validation des rapports',
  'pointeur-photos': 'Photos chantier',
  'qhse-pays': 'QHSE',
  'admin-dashboard': 'Admin · Tableau de bord',
  'admin-users': 'Admin · Utilisateurs',
  'admin-user-profile': 'Admin · Fiche utilisateur',
  'admin-permissions': 'Admin · Permissions',
  'admin-ai-settings': 'Admin · IA',
  'admin-fuel-config': 'Admin · Carburant',
  'admin-budget-control-config': 'Admin · Config. contrôle budgétaire',
  'admin-general-settings': 'Admin · Paramètres généraux',
  'admin-system': 'Admin · Système',
  'admin-audit-logs': "Admin · Journal d'audit",
  'admin-feedback': 'Admin · Feedbacks',
  'admin-email-settings': 'Admin · Emails',
  'admin-notification-settings': 'Admin · Notifications',
  'admin-send-notifications': 'Admin · Envoi de notifications'
}

export const pageLabel = (routeName, path) => PAGE_LABELS[routeName] || path || '—'

const MODULE_LABELS = {
  resource: 'Ressources',
  dailyexpensereport: 'Dépenses journalières',
  sitereport: 'Rapports de chantier',
  monthlyplanline: 'Planning mensuel (lignes)',
  monthlyplan: 'Planning mensuel',
  project: 'Projets',
  auth: 'Authentification',
  personnel: 'Personnel',
  materiel: 'Matériel',
  budget_control: 'Contrôle budgétaire',
  users: 'Gestion des utilisateurs',
  user: 'Comptes utilisateurs',
  system: 'Système',
  permissions: 'Permissions',
  qhse: 'QHSE'
}

export const moduleLabel = (module) => MODULE_LABELS[module] || (module ? module.replace(/_/g, ' ') : 'Général')

const ACTION_LABELS = {
  'auth.login': 'Connexion',
  'auth.logout': 'Déconnexion',
  'auth.impersonate': 'Usurpation de compte',
  'security.unlock': 'Déblocage de sécurité',
  reallocation: 'Réallocation',
  user_locked: 'Verrouillage de compte',
  user_unlocked: 'Déverrouillage de compte',
  user_sessions_revoked: 'Déconnexion forcée',
  user_session_revoked: 'Session révoquée',
  user_password_reset_sent: 'Lien de réinitialisation envoyé',
  user_notified: 'Notification envoyée',
  user_activity_exported: "Export d'activité"
}

// Type d'action : pilote l'icône et la couleur dans les timelines
export const actionKind = (action = '') => {
  if (action.startsWith('auth.')) return action === 'auth.logout' ? 'logout' : action === 'auth.impersonate' ? 'impersonate' : 'login'
  if (action.endsWith('created')) return 'created'
  if (action.endsWith('deleted')) return 'deleted'
  if (action.endsWith('updated')) return 'updated'
  return 'other'
}

export const KIND_META = {
  login: { label: 'Connexion', color: '#10b981' },
  logout: { label: 'Déconnexion', color: '#64748b' },
  impersonate: { label: 'Usurpation', color: '#f59e0b' },
  created: { label: 'Création', color: '#4f46e5' },
  updated: { label: 'Modification', color: '#0ea5e9' },
  deleted: { label: 'Suppression', color: '#ef4444' },
  other: { label: 'Action', color: '#8b5cf6' }
}

// Descriptions générées par le trait Auditable : « Modification de DailyExpenseReport: 1174 »
const MODEL_LABELS = {
  DailyExpenseReport: 'rapport de dépenses',
  SiteReport: 'rapport de chantier',
  MonthlyPlanLine: 'ligne de planning mensuel',
  MonthlyPlan: 'planning mensuel',
  Resource: 'ressource',
  Project: 'projet',
  Personnel: 'personnel',
  Materiel: 'matériel',
  User: 'utilisateur'
}

export const humanizeDescription = (description = '') =>
  String(description || '').replace(/\b(Création|Modification|Suppression) de (\w+):\s*(.*)$/, (match, verb, model, subject) =>
    MODEL_LABELS[model] ? `${verb} — ${MODEL_LABELS[model]} ${/^\d+$/.test(subject) ? `#${subject}` : subject}` : match)

export const actionLabel = (action = '') => {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action]
  const kind = actionKind(action)
  return KIND_META[kind]?.label || action
}

// ─── Formats ────────────────────────────────────────────────

export const initials = (name) => String(name || '?').trim().split(/\s+/).map(p => p.charAt(0)).join('').toUpperCase().substring(0, 2)

// Dégradé d'avatar stable dérivé du nom
const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'], ['#0ea5e9', '#6366f1'], ['#10b981', '#0ea5e9'], ['#f59e0b', '#ef4444'],
  ['#ec4899', '#8b5cf6'], ['#14b8a6', '#22c55e'], ['#3b82f6', '#06b6d4'], ['#8b5cf6', '#d946ef']
]
export const avatarGradient = (name) => {
  const hash = String(name || '').split('').reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7)
  const [a, b] = AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length]
  return `linear-gradient(135deg, ${a}, ${b})`
}

const toDate = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'))
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatDate = (value) => toDate(value)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) || '—'

export const formatDateTime = (value) => toDate(value)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || '—'

export const formatTime = (value) => toDate(value)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) || ''

export const relativeTime = (value) => {
  const date = toDate(value)
  if (!date) return 'Jamais'
  const seconds = Math.round((Date.now() - date.getTime()) / 1000)
  if (seconds < 0) return "À l'instant"
  if (seconds < 60) return "À l'instant"
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `Il y a ${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Il y a ${hours} h`
  const days = Math.round(hours / 24)
  if (days === 1) return 'Hier'
  if (days < 30) return `Il y a ${days} j`
  const months = Math.round(days / 30)
  if (months < 12) return `Il y a ${months} mois`
  const years = Math.round(months / 12)
  return `Il y a ${years} an${years > 1 ? 's' : ''}`
}

export const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0))
  if (s < 60) return `${s} s`
  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours} h`
}

export const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(Number(value) || 0)

export const PRESENCE_META = {
  online: { label: 'En ligne', color: '#10b981' },
  active: { label: 'Actif', color: '#0ea5e9' },
  idle: { label: 'Peu actif', color: '#f59e0b' },
  inactive: { label: 'Inactif', color: '#94a3b8' },
  never: { label: 'Jamais connecté', color: '#cbd5e1' },
  locked: { label: 'Verrouillé', color: '#ef4444' }
}

// Statut de présence dérivé de la dernière activité
const presenceKey = (lockedUntil, lastSeenAt) => {
  const locked = toDate(lockedUntil)
  if (locked && locked > new Date()) return 'locked'
  const seen = toDate(lastSeenAt)
  if (!seen) return 'never'
  const minutes = (Date.now() - seen.getTime()) / 60000
  if (minutes <= 5) return 'online'
  if (minutes <= 60 * 24 * 7) return 'active'
  if (minutes <= 60 * 24 * 30) return 'idle'
  return 'inactive'
}

export const presenceOf = ({ lockedUntil, lastSeenAt }) => {
  const key = presenceKey(lockedUntil, lastSeenAt)
  return { key, ...PRESENCE_META[key] }
}
